import { useEffect } from 'react';
import type { IncomeRoll } from '@guess/engine';
import type { GameState } from '../app/state';
import { playerName, seatIndex } from '../app/state';
import { sfx } from '../audio/sfx';
import { Seat } from './Seat';
import { money } from './format';
import s from './ui.module.scss';

/**
 * Le revenu aux des.
 *
 * Le README dit pourquoi il existe : c'est le garde-fou anti-ruine, et il vaut
 * mieux qu'un plancher parce que lancer les des est un RITUEL que le dernier
 * fait comme les autres. Un rituel qui n'apparaitrait qu'en ligne de compte
 * n'en serait pas un — d'ou ces des qui roulent vraiment a l'ecran, pour tout
 * le monde, a chaque manche.
 *
 * Les `notes` viennent des pouvoirs (Renfort, Confiscation) et expliquent
 * pourquoi quelqu'un lance trois des ou un seul. On les affiche telles quelles :
 * c'est le serveur qui les redige, et c'est lui qui fait foi.
 */
export interface DiceProps {
  state: GameState;
  rolls: readonly IncomeRoll[];
}

const PIPS = ['', '⚀', '⚁', '⚂', '⚃', '⚄', '⚅'];

export function Dice({ state, rolls }: DiceProps) {
  // Ce composant est demonte/remonte a chaque entree en phase de mise (le
  // rendu de l'ecran est conditionne par `state.income`) : un son au montage
  // tombe donc naturellement une fois par manche, sans jeton dedie.
  useEffect(() => {
    sfx.diceRoll();
  }, []);

  return (
    <section className={s.dice}>
      <h2 className={s.diceTitle}>Revenu</h2>

      <ul className={s.diceList}>
        {rolls.map((roll) => (
          <li key={roll.playerId} className={s.diceRow}>
            <Seat
              className={s.diceSeat}
              name={playerName(state, roll.playerId)}
              seatIndex={seatIndex(state, roll.playerId)}
              bot={state.players.find((p) => p.id === roll.playerId)?.bot ?? false}
              you={roll.playerId === state.you}
            />

            <span className={s.diceFaces} aria-label={`des : ${roll.rolls.join(', ')}`}>
              {roll.rolls.map((face, index) => (
                <span
                  key={index}
                  className={s.diceFace}
                  // Chaque de tombe legerement apres le precedent : un jet
                  // simultane ressemble a un affichage, pas a un lancer.
                  style={{ animationDelay: `calc(var(--dur-fast) * ${index})` }}
                >
                  {PIPS[face] ?? face}
                </span>
              ))}
            </span>

            <span className={s.diceTotal}>+{money(roll.total)}</span>

            {roll.notes.length > 0 ? (
              <span className={s.diceNotes}>{roll.notes.join(' · ')}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
